import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../input/input.tsx';
import type { DataTableSearch } from './types.ts';

/*
 * Long enough that a word is typed before the first request goes out, short
 * enough that the list has moved by the time the eye reaches it.
 */
export const SEARCH_DEBOUNCE_MS = 250;

export interface TableSearchProps extends DataTableSearch {
  label?: string;
}

/**
 * The search field in the table's toolbar. It debounces; the filtering itself
 * belongs to whoever owns the rows.
 */
export function TableSearch({
  value,
  onChange,
  placeholder = 'Search',
  label,
}: TableSearchProps) {
  const [draft, setDraft] = useState(value);
  /*
   * The last value this field put into the world. Anything else arriving in
   * `value` came from the caller — "Clear filters" — and replaces what is being
   * typed; the caller echoing our own value back does not.
   */
  const emitted = useRef(value);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(() => {
    if (draft === emitted.current) {
      return;
    }
    const timer = setTimeout(() => {
      emitted.current = draft;
      onChangeRef.current(draft);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [draft]);

  return (
    <span data-slot='data-table-search' className='relative inline-flex items-center'>
      <Search
        aria-hidden='true'
        className='pointer-events-none absolute left-3 size-3.5 text-ink-muted'
      />
      <Input
        type='search'
        aria-label={label ?? placeholder}
        placeholder={placeholder}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        className='w-60 pl-8.5'
      />
    </span>
  );
}
